(() => {
  'use strict';

  if (window.__WIHeaderV3Loaded) return;
  window.__WIHeaderV3Loaded = true;

  const ROOT_SELECTOR = '[data-wi-header]';
  const initializedRoots = new WeakSet();

  const initWiHeader = (root) => {
    if (!root || initializedRoots.has(root)) return;

    initializedRoots.add(root);
    root.dataset.wiHeaderInitialized = 'true';

    const sectionId = root.id.replace(/^WIHeader-/, '');
    const isSticky = root.dataset.sticky === 'true';
    const isOverlay = root.dataset.overlay === 'true';
    const desktopBreakpoint = Math.max(
      Number(root.dataset.desktopBreakpoint) || 1360,
      990
    );

    const topbarTrack = root.querySelector(
      '[data-wi-header-topbar-track]'
    );
    const nav = root.querySelector('[data-wi-header-nav]');
    const logo = root.querySelector('[data-wi-header-logo]');
    const icons = root.querySelector('[data-wi-header-icons]');
    const desktopMenus = Array.from(
      root.querySelectorAll('.wi-header__nav-details')
    );
    const mobileMenu = root.querySelector(
      '[data-wi-header-mobile-menu]'
    );
    const mobileCloseButtons = Array.from(
      root.querySelectorAll('[data-wi-header-mobile-close]')
    );

    const cleanupCallbacks = [];
    let destroyed = false;

    const on = (target, type, handler, options) => {
      if (!target) return;

      target.addEventListener(type, handler, options);
      cleanupCallbacks.push(() => {
        target.removeEventListener(type, handler, options);
      });
    };

    const runInFrame = (callback) => {
      let frameId = null;

      const request = () => {
        if (frameId !== null || destroyed) return;

        frameId = window.requestAnimationFrame(() => {
          frameId = null;
          callback();
        });
      };

      cleanupCallbacks.push(() => {
        if (frameId !== null) {
          window.cancelAnimationFrame(frameId);
        }
      });

      return request;
    };

    document.body.classList.add('wi-has-wi-header');
    document.body.classList.toggle(
      'wi-header-needs-offset',
      isSticky && !isOverlay
    );
    document.body.classList.toggle(
      'wi-header-overlay-page',
      isSticky && isOverlay
    );

    root.classList.toggle(
      'hide-mobile-secondary-icons',
      root.dataset.hideMobileSecondaryIcons === 'true'
    );

    let lastHeaderHeight = -1;

    const measureHeaderHeight = () => {
      const height = Math.round(root.offsetHeight || 0);

      if (height === lastHeaderHeight) return;

      lastHeaderHeight = height;
      document.documentElement.style.setProperty(
        '--wi-header-height',
        `${height}px`
      );
    };

    const requestHeaderHeight = runInFrame(measureHeaderHeight);

    requestHeaderHeight();
    on(window, 'load', requestHeaderHeight, { once: true });

    if ('ResizeObserver' in window) {
      const headerResizeObserver = new ResizeObserver(
        requestHeaderHeight
      );

      headerResizeObserver.observe(root);
      cleanupCallbacks.push(() => {
        headerResizeObserver.disconnect();
      });
    } else {
      on(window, 'resize', requestHeaderHeight, {
        passive: true
      });
    }

    if (isSticky) {
      let lastScrolledState = null;

      const updateScrolledState = () => {
        const isScrolled = window.scrollY > 4;

        if (isScrolled === lastScrolledState) return;

        lastScrolledState = isScrolled;
        root.classList.toggle('is-scrolled', isScrolled);
      };

      const requestScrolledState = runInFrame(
        updateScrolledState
      );

      updateScrolledState();
      on(window, 'scroll', requestScrolledState, {
        passive: true
      });
    }

    const setTopbarDuration = () => {
      if (!topbarTrack) return;

      const groups = topbarTrack.querySelectorAll(
        '.wi-header__topbar-group'
      );

      if (groups.length < 2) return;

      const speed = Math.max(
        Number(topbarTrack.dataset.scrollSpeed) || 20,
        8
      );
      const distance = Math.max(groups[0].scrollWidth, 1);
      const duration = Math.max(distance / speed, 20);

      topbarTrack.style.setProperty(
        '--wi-header-topbar-duration',
        `${duration}s`
      );
    };

    setTopbarDuration();
    on(window, 'load', setTopbarDuration, { once: true });

    const closeDesktopMenus = (exceptMenu = null) => {
      desktopMenus.forEach((menu) => {
        if (menu === exceptMenu) return;

        menu.open = false;
        menu.classList.remove('is-hover-open');
      });
    };

    const alignMegaCards = (menu) => {
      if (
        !menu ||
        !menu.open ||
        root.classList.contains('is-compact')
      ) {
        return;
      }

      const cardsInner = menu.querySelector(
        '.wi-header__mega-inner--cards'
      );
      const cardsGrid = menu.querySelector(
        '.wi-header__mega-card-grid'
      );

      if (!cardsInner || !cardsGrid || !nav) return;

      cardsInner.style.setProperty(
        '--wi-header-card-grid-offset',
        '0px'
      );

      const innerRect = cardsInner.getBoundingClientRect();
      const navRect = nav.getBoundingClientRect();
      const innerStyles = window.getComputedStyle(cardsInner);
      const paddingLeft =
        Number.parseFloat(innerStyles.paddingLeft) || 0;
      const paddingRight =
        Number.parseFloat(innerStyles.paddingRight) || 0;
      const availableWidth = Math.max(
        cardsInner.clientWidth - paddingLeft - paddingRight,
        0
      );
      const maximumOffset = Math.max(
        availableWidth - cardsGrid.offsetWidth,
        0
      );
      const requestedOffset =
        navRect.left - innerRect.left - paddingLeft;
      const finalOffset = Math.max(
        0,
        Math.min(requestedOffset, maximumOffset)
      );

      cardsInner.style.setProperty(
        '--wi-header-card-grid-offset',
        `${Math.round(finalOffset)}px`
      );
    };

    let lastDesktopState = null;
    let lastCompactState = null;

    const updateResponsiveState = () => {
      const isDesktop =
        window.innerWidth >= desktopBreakpoint;

      root.classList.toggle('is-desktop', isDesktop);

      /*
        Remove compact mode before measuring the desktop layout.
        This measurement only runs on resize/load, never on scroll.
      */
      root.classList.remove('is-compact');

      let hasCollision = false;

      if (isDesktop && nav && logo && icons) {
        const navList = nav.querySelector(
          '.wi-header__nav-list'
        );
        const navRect = (navList || nav).getBoundingClientRect();
        const logoRect = logo.getBoundingClientRect();
        const iconsRect = icons.getBoundingClientRect();
        const safeGap = 24;

        hasCollision =
          logoRect.right + safeGap > navRect.left ||
          navRect.right + safeGap > iconsRect.left;
      }

      const useCompactMenu = !isDesktop || hasCollision;

      root.classList.toggle('is-compact', useCompactMenu);

      if (useCompactMenu) {
        closeDesktopMenus();
      } else {
        desktopMenus.forEach((menu) => {
          if (menu.open) alignMegaCards(menu);
        });
      }

      if (
        isDesktop !== lastDesktopState ||
        useCompactMenu !== lastCompactState
      ) {
        requestHeaderHeight();
      }

      lastDesktopState = isDesktop;
      lastCompactState = useCompactMenu;
    };

    const requestResponsiveState = runInFrame(
      updateResponsiveState
    );

    updateResponsiveState();
    on(window, 'resize', requestResponsiveState, {
      passive: true
    });
    on(window, 'load', requestResponsiveState, {
      once: true
    });

    const updateHeaderState = () => {
      const menuIsOpen =
        desktopMenus.some((menu) => menu.open) ||
        Boolean(mobileMenu?.open);

      root.classList.toggle('is-menu-open', menuIsOpen);
    };

    const closeMobileSubmenus = () => {
      if (!mobileMenu) return;

      mobileMenu
        .querySelectorAll('.wi-header__mobile-details[open]')
        .forEach((submenu) => {
          submenu.open = false;
        });
    };

    const updateMobileDrawerLock = () => {
      const isOpen = Boolean(mobileMenu?.open);

      /*
        Always start the tablet/mobile drawer with every collection closed.
        This also clears any submenu the visitor opened before closing the drawer.
      */
      closeMobileSubmenus();

      document.documentElement.classList.toggle(
        'wi-mobile-menu-open',
        isOpen
      );
      document.documentElement.classList.toggle(
        `wi-mobile-menu-open-${sectionId}`,
        isOpen
      );

      updateHeaderState();
    };

    const closeMobileMenu = () => {
      if (!mobileMenu || !mobileMenu.open) return;

      mobileMenu.open = false;
      updateMobileDrawerLock();
    };

    on(mobileMenu, 'toggle', updateMobileDrawerLock);

    mobileCloseButtons.forEach((button) => {
      on(button, 'click', closeMobileMenu);
    });

    desktopMenus.forEach((menu) => {
      let closeTimer = null;

      const openMenu = () => {
        window.clearTimeout(closeTimer);
        closeDesktopMenus(menu);

        menu.open = true;
        menu.classList.add('is-hover-open');
        updateHeaderState();

        window.requestAnimationFrame(() => {
          alignMegaCards(menu);
        });
      };

      const scheduleClose = () => {
        window.clearTimeout(closeTimer);

        closeTimer = window.setTimeout(() => {
          if (
            menu.matches(':hover') ||
            menu.contains(document.activeElement)
          ) {
            return;
          }

          menu.open = false;
          menu.classList.remove('is-hover-open');
          updateHeaderState();
        }, 110);
      };

      on(menu, 'mouseenter', openMenu);
      on(menu, 'mouseleave', scheduleClose);

      on(menu, 'toggle', () => {
        if (menu.open) {
          window.requestAnimationFrame(() => {
            alignMegaCards(menu);
          });
        } else {
          menu.classList.remove('is-hover-open');
        }

        updateHeaderState();
      });

      cleanupCallbacks.push(() => {
        window.clearTimeout(closeTimer);
      });
    });

    const handleFocusOut = () => {
      window.setTimeout(() => {
        if (destroyed) return;

        desktopMenus.forEach((menu) => {
          if (
            !menu.matches(':hover') &&
            !menu.contains(document.activeElement)
          ) {
            menu.open = false;
            menu.classList.remove('is-hover-open');
          }
        });

        updateHeaderState();
      }, 0);
    };

    on(root, 'focusout', handleFocusOut);

    const handleDocumentClick = (event) => {
      if (root.contains(event.target)) return;

      closeDesktopMenus();
      closeMobileMenu();
      updateHeaderState();
    };

    const handleDocumentKeydown = (event) => {
      if (event.key !== 'Escape') return;

      closeDesktopMenus();
      closeMobileMenu();
      updateHeaderState();
    };

    on(document, 'click', handleDocumentClick);
    on(document, 'keydown', handleDocumentKeydown);

    root.wiHeaderCleanup = () => {
      if (destroyed) return;

      destroyed = true;
      cleanupCallbacks.forEach((cleanup) => cleanup());

      document.body.classList.remove(
        'wi-header-needs-offset',
        'wi-header-overlay-page'
      );
      document.documentElement.classList.remove(
        'wi-mobile-menu-open',
        `wi-mobile-menu-open-${sectionId}`
      );

      root.dataset.wiHeaderInitialized = 'false';
      delete root.wiHeaderCleanup;
    };
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) {
      initWiHeader(scope);
    }

    scope
      .querySelectorAll?.(ROOT_SELECTOR)
      .forEach(initWiHeader);
  };

  initializeAll();

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const root =
        event.target.matches?.(ROOT_SELECTOR)
          ? event.target
          : event.target.querySelector?.(ROOT_SELECTOR);

      root?.wiHeaderCleanup?.();
    }
  );
})();
/* ==========================================================================
   WI Header — inline submitted search results
   Prevents the search form from leaving the popup. A submitted query is sent
   to Shopify's predictive-search JSON endpoint and the returned products are
   rendered in the existing scrollable results area.
   ========================================================================== */
(() => {
  'use strict';

  if (window.__WIHeaderInlineSearchLoaded) return;
  window.__WIHeaderInlineSearchLoaded = true;

  const FORM_SELECTOR =
    '[data-wi-header] .wi-search-modal form[action*="/search"]';

  const requestControllers = new WeakMap();
  const requestNumbers = new WeakMap();

  const normaliseImageUrl = (value) => {
    if (!value) return '';

    const source =
      typeof value === 'string'
        ? value
        : value.url || value.src || '';

    if (!source) return '';
    if (source.startsWith('//')) return `https:${source}`;

    return source;
  };

  const createElement = (tagName, className, text) => {
    const element = document.createElement(tagName);

    if (className) element.className = className;
    if (text != null) element.textContent = text;

    return element;
  };

  const getSearchElements = (form) => {
    const input = form.querySelector(
      'input[type="search"][name="q"], input[name="q"]'
    );

    const predictiveRoot =
      form.closest('predictive-search') ||
      form.querySelector('predictive-search');

    let resultsContainer = form.querySelector(
      '[data-predictive-search]'
    );

    if (!resultsContainer) {
      resultsContainer = createElement(
        'div',
        'predictive-search predictive-search--header'
      );

      resultsContainer.dataset.predictiveSearch = '';

      const field = form.querySelector('.field');
      field?.insertAdjacentElement('afterend', resultsContainer);
    }

    const status = form.querySelector(
      '.predictive-search-status, [role="status"]'
    );

    return {
      input,
      predictiveRoot,
      resultsContainer,
      status
    };
  };

  const setPanelOpen = (
    predictiveRoot,
    input,
    isOpen
  ) => {
    if (predictiveRoot) {
      predictiveRoot.toggleAttribute('open', isOpen);
      predictiveRoot.classList.toggle(
        'is-inline-search-results',
        isOpen
      );
    }

    input?.setAttribute(
      'aria-expanded',
      isOpen ? 'true' : 'false'
    );
  };

  const renderLoading = (container, query) => {
    container.replaceChildren();

    const state = createElement(
      'div',
      'wi-search-inline-state wi-search-inline-state--loading'
    );

    const spinner = createElement(
      'span',
      'wi-search-inline-state__spinner'
    );

    spinner.setAttribute('aria-hidden', 'true');

    const copy = createElement(
      'span',
      'wi-search-inline-state__copy',
      `Searching for “${query}”…`
    );

    state.append(spinner, copy);
    container.append(state);
  };

  const renderMessage = (
    container,
    heading,
    copy,
    className = ''
  ) => {
    container.replaceChildren();

    const state = createElement(
      'div',
      `wi-search-inline-state ${className}`.trim()
    );

    state.append(
      createElement(
        'strong',
        'wi-search-inline-state__heading',
        heading
      ),
      createElement(
        'p',
        'wi-search-inline-state__copy',
        copy
      )
    );

    container.append(state);
  };

  const renderProducts = (
    container,
    products,
    query
  ) => {
    container.replaceChildren();

    if (!products.length) {
      renderMessage(
        container,
        'No products found',
        `We could not find a product matching “${query}”. Try another word or a shorter phrase.`,
        'wi-search-inline-state--empty'
      );

      return;
    }

    const section = createElement(
      'section',
      'wi-search-inline-results'
    );

    section.setAttribute(
      'aria-label',
      `Search results for ${query}`
    );

    const header = createElement(
      'div',
      'wi-search-inline-results__header'
    );

    const headingWrap = createElement(
      'div',
      'wi-search-inline-results__heading-wrap'
    );

    headingWrap.append(
      createElement(
        'span',
        'wi-search-inline-results__eyebrow',
        'Search results'
      ),
      createElement(
        'h3',
        'wi-search-inline-results__title',
        `Results for “${query}”`
      )
    );

    const count = createElement(
      'span',
      'wi-search-inline-results__count',
      `${products.length} ${
        products.length === 1 ? 'product' : 'products'
      }`
    );

    header.append(headingWrap, count);

    const list = createElement(
      'ul',
      'wi-search-inline-results__grid'
    );

    list.setAttribute('role', 'list');

    products.forEach((product) => {
      const item = createElement(
        'li',
        'wi-search-inline-results__item'
      );

      const link = createElement(
        'a',
        'wi-search-inline-results__card'
      );

      link.href = product.url || '#';

      const media = createElement(
        'span',
        'wi-search-inline-results__media'
      );

      const imageUrl = normaliseImageUrl(
        product.featured_image || product.image
      );

      if (imageUrl) {
        const image = document.createElement('img');

        image.src = imageUrl;
        image.alt =
          product.featured_image?.alt ||
          product.title ||
          '';
        image.loading = 'lazy';
        image.decoding = 'async';

        media.append(image);
      } else {
        media.append(
          createElement(
            'span',
            'wi-search-inline-results__placeholder'
          )
        );
      }

      const content = createElement(
        'span',
        'wi-search-inline-results__content'
      );

      if (product.vendor) {
        content.append(
          createElement(
            'span',
            'wi-search-inline-results__vendor',
            product.vendor
          )
        );
      }

      content.append(
        createElement(
          'span',
          'wi-search-inline-results__product-title',
          product.title || 'View product'
        )
      );

      const action = createElement(
        'span',
        'wi-search-inline-results__action',
        'View product'
      );

      action.setAttribute('aria-hidden', 'true');
      content.append(action);

      link.append(media, content);
      item.append(link);
      list.append(item);
    });

    section.append(header, list);
    container.append(section);
  };

  const getSuggestUrl = (form, query) => {
    const action = new URL(
      form.getAttribute('action') || '/search',
      window.location.origin
    );

    action.pathname = action.pathname
      .replace(/\/search\/?$/, '/search/suggest.json');

    action.search = '';
    action.searchParams.set('q', query);
    action.searchParams.set(
      'resources[type]',
      'product'
    );
    action.searchParams.set(
      'resources[limit]',
      '10'
    );
    action.searchParams.set(
      'resources[options][unavailable_products]',
      'last'
    );

    return action.toString();
  };

  const submitInlineSearch = async (
    form,
    query,
    elements
  ) => {
    const {
      input,
      predictiveRoot,
      resultsContainer,
      status
    } = elements;

    requestControllers.get(form)?.abort();

    const controller = new AbortController();
    const requestNumber =
      (requestNumbers.get(form) || 0) + 1;

    requestControllers.set(form, controller);
    requestNumbers.set(form, requestNumber);

    setPanelOpen(predictiveRoot, input, true);
    predictiveRoot?.setAttribute('loading', '');
    renderLoading(resultsContainer, query);

    if (status) {
      status.textContent = `Searching for ${query}`;
      status.removeAttribute('aria-hidden');
    }

    try {
      const response = await fetch(
        getSuggestUrl(form, query),
        {
          signal: controller.signal,
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `Search request failed with status ${response.status}.`
        );
      }

      const data = await response.json();

      if (
        requestNumbers.get(form) !== requestNumber
      ) {
        return;
      }

      const products =
        data?.resources?.results?.products || [];

      renderProducts(
        resultsContainer,
        products,
        query
      );

      if (status) {
        status.textContent = `${products.length} ${
          products.length === 1
            ? 'product'
            : 'products'
        } found for ${query}`;
      }
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error(
        '[WI Header] Inline search failed.',
        error
      );

      renderMessage(
        resultsContainer,
        'Search is temporarily unavailable',
        'Please try again in a moment.',
        'wi-search-inline-state--error'
      );

      if (status) {
        status.textContent =
          'Search is temporarily unavailable.';
      }
    } finally {
      if (
        requestNumbers.get(form) === requestNumber
      ) {
        predictiveRoot?.removeAttribute('loading');
      }
    }
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest?.(
        FORM_SELECTOR
      );

      if (!form) return;

      const elements = getSearchElements(form);
      const query =
        elements.input?.value.trim() || '';

      /*
        Capture the submit before Dawn/search-form can perform its normal
        page navigation. Both Enter and the search icon now remain in-modal.
      */
      event.preventDefault();
      event.stopImmediatePropagation();

      if (!query) {
        setPanelOpen(
          elements.predictiveRoot,
          elements.input,
          false
        );

        elements.input?.focus();
        return;
      }

      submitInlineSearch(
        form,
        query,
        elements
      );
    },
    true
  );

  document.addEventListener(
    'input',
    (event) => {
      const input = event.target.closest?.(
        `${FORM_SELECTOR} input[type="search"][name="q"], ${FORM_SELECTOR} input[name="q"]`
      );

      if (!input) return;

      const predictiveRoot =
        input.closest('predictive-search');

      /*
        Returning to typing hands control back to the theme's live
        predictive-search component.
      */
      predictiveRoot?.classList.remove(
        'is-inline-search-results'
      );
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const resetButton = event.target.closest?.(
        '[data-wi-header] .wi-search-modal .reset__button'
      );

      if (!resetButton) return;

      const form = resetButton.closest('form');

      requestControllers.get(form)?.abort();

      window.requestAnimationFrame(() => {
        const elements = getSearchElements(form);

        elements.resultsContainer?.replaceChildren();

        setPanelOpen(
          elements.predictiveRoot,
          elements.input,
          false
        );
      });
    },
    true
  );
})();
